<template>
  <div class="audience-dashboard" :class="{ 'panel-open': showCriteriaPanel }">
    <!-- Criteria Builder Side Panel -->
    <transition name="slide">
      <div v-if="showCriteriaPanel" class="criteria-panel">
        <div class="criteria-panel-header">
          <h5 class="mb-0">
            <i class="fa fa-filter me-2"></i>
            Segment Builder
          </h5>
          <button type="button" class="btn-close" @click="closeCriteriaPanel"></button>
        </div>
        <div class="criteria-panel-body">
          <!-- Filtered Count -->
          <div class="filtered-count-card mb-3">
            <div class="d-flex justify-content-between align-items-center">
              <span class="text-muted small">Filtered Count</span>
              <span v-if="loadingFiltered" class="spinner-border spinner-border-sm text-primary"></span>
            </div>
            <h3 class="mb-0 mt-1">{{ formatNumber(filteredCount) }}</h3>
            <small class="text-muted">
              {{ getFilteredPercentage() }}% of total
            </small>
          </div>

          <!-- Criteria List -->
          <div class="criteria-list">
            <div class="d-flex justify-content-between align-items-center">
              <label class="form-label small text-muted text-uppercase">Applied Criteria</label>
              <button class="btn btn-sm btn-primary p-0 mb-2" :disabled="loadingFiltered || !criteriaItems?.length" @click="runFilteredHistogram" :style="{ width: '40px' }">
                Run
              </button>
            </div>
            <div v-if="criteriaItems.length === 0" class="text-muted small py-2">
              Click on chart segments to add criteria
            </div>
            <div
              v-for="(item, index) in criteriaItems"
              :key="index"
              class="criteria-item"
            >
              <div class="criteria-item-content">
                <span class="criteria-attribute">{{ formatAttributeName(item.attribute) }}</span>
                <span class="criteria-operator">=</span>
                <span class="criteria-value">{{ item.value }}</span>
              </div>
              <button
                type="button"
                class="btn btn-sm btn-link text-danger p-0"
                @click="removeCriterion(index)"
                title="Remove"
              >
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>

          <!-- Actions -->
          <div class="criteria-actions mt-4">
            <button
              class="btn btn-primary w-100 mb-2"
              @click="saveAsAudience"
              :disabled="criteriaItems.length === 0"
            >
              <i class="fa fa-save me-2"></i>
              Save as Audience
            </button>
            <button
              class="btn btn-outline-secondary w-100"
              @click="clearAllCriteria"
              :disabled="criteriaItems.length === 0"
            >
              <i class="fa fa-trash me-2"></i>
              Clear All
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Main Content Area -->
    <div class="dashboard-content">
      <!-- Data Environment & Dataset Selection -->
      <div class="card mb-4">
        <div class="card-body">
          <div class="row align-items-end">
            <div class="col-md-4">
              <label class="form-label">Data Environment</label>
              <select
                class="form-select"
                v-model="selectedDataEnvironment"
                @change="onDataEnvironmentChange"
                :disabled="loadingEnvironments"
              >
                <option value="">Select data environment...</option>
                <option
                  v-for="env in dataEnvironments"
                  :key="env.name"
                  :value="env.name"
                >
                  {{ env.name }}
                </option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">Dataset</label>
              <select
                class="form-select"
                v-model="selectedDataset"
                @change="onDatasetChange"
                :disabled="!selectedDataEnvironment || loadingDatasets"
              >
                <option value="">Select dataset...</option>
                <option v-for="ds in datasets" :key="ds" :value="ds">
                  {{ ds }}
                </option>
              </select>
            </div>
            <div class="col-md-2">
              <label class="form-label">Query Engine</label>
              <select class="form-select" v-model="queryEngine">
                <option value="athena">Athena</option>
                <option value="analytics">Analytics</option>
              </select>
            </div>
            <div class="col-md-2">
              <button
                class="btn btn-primary w-100"
                @click="runFullHistogram"
                :disabled="!selectedDataset || loadingHistogram"
              >
                <span v-if="loadingHistogram" class="spinner-border spinner-border-sm me-2"></span>
                <i v-else class="fa fa-chart-bar me-2"></i>
                {{ loadingHistogram ? 'Loading...' : 'Run Analysis' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Alert -->
      <div v-if="error" class="alert alert-danger alert-dismissible fade show">
        {{ error }}
        <button type="button" class="btn-close" @click="error = null"></button>
      </div>

      <!-- Loading State -->
      <div v-if="loadingHistogram" class="text-center py-5">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-3 text-muted">Analyzing audience composition...</p>
      </div>

      <!-- Results -->
      <div v-else-if="hasResults">
      <!-- Total Count Card -->
      <div class="total-count-card mb-4">
        <div class="card bg-primary text-white">
          <div class="card-body text-center py-4">
            <h6 class="text-uppercase text-white-50 mb-2">
              {{ criteriaItems.length > 0 ? 'Filtered Audience Size' : 'Total Audience Size' }}
            </h6>
            <div class="d-flex align-items-center justify-content-center">
              <h1 class="display-4 fw-bold mb-0">{{ formatNumber(displayCount) }}</h1>
              <span v-if="loadingFiltered" class="spinner-border spinner-border-sm text-white ms-3"></span>
            </div>
            <small v-if="criteriaItems.length > 0" class="text-white-50">
              {{ getFilteredPercentage() }}% of {{ formatNumber(totalCount) }} total
            </small>
            <!-- Query Statistics -->
            <div v-if="queryStatistics" class="query-stats mt-2">
              <span class="stat-badge">
                <i class="fa fa-clock me-1"></i>{{ formatExecutionTime(queryStatistics.execution_time_millis) }}
              </span>
              <span class="stat-badge">
                <i class="fa fa-database me-1"></i>{{ formatBytes(queryStatistics.data_scanned_bytes) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Primary Attributes - Demographics -->
      <div class="charts-section position-relative">
        <!-- Loading Overlay -->
        <transition name="fade">
          <div v-if="loadingFiltered" class="charts-loading-overlay">
            <div class="loading-content">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mt-2 mb-0 text-muted">Updating results...</p>
            </div>
          </div>
        </transition>

        <AttributeCharts
          :attributes="sortedAttributes"
          :selected="criteriaItems"
          :animate="!initialLoadComplete"
          :record-total="displayCount"
          clickable
          @select="onChartSelect"
        />
      </div>

      <!-- Click hint when no panel is open -->
      <div v-if="!showCriteriaPanel" class="click-hint text-center mt-3">
        <small class="text-muted">
          <i class="fa fa-hand-pointer me-1"></i>
          Click on any chart segment to start building a segment
        </small>
      </div>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-5">
        <i class="fa fa-chart-pie fa-4x text-muted mb-4"></i>
        <h4 class="text-muted">Audience Composition Dashboard</h4>
        <p class="text-muted">
          Select a data environment and dataset, then click "Run Analysis" to visualize your audience composition.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import AttributeCharts from './AttributeCharts.vue'

export default {
  name: 'AudienceDashboard',
  components: {
    AttributeCharts,
  },
  data() {
    return {
      loadingEnvironments: false,
      loadingDatasets: false,
      loadingHistogram: false,
      loadingFiltered: false,
      error: null,
      selectedDataEnvironment: '',
      selectedDataset: '',
      queryEngine: 'analytics',
      datasets: [],
      totalCount: 0,
      filteredCount: 0,
      singleAttributes: {},
      combinationAttributes: {},
      hasResults: false,

      // Filtered criterion results
      filteredSingleAttributes: null,
      filteredCombinationAttributes: null,

      // Query statistics
      queryStatistics: null,

      // Animation control - only animate on initial load/re-run
      initialLoadComplete: false,

      // Criteria panel state
      showCriteriaPanel: false,
      criteriaItems: [],
      // Attribute importance/priority for layout
      attributePriority: {
        'gender': 1,
        'age_range': 2,
        'income_range': 3,
        'ethnicity': 4,
        'education': 5,
        'marital_state': 6,
        'children': 7,
        'state': 8,
        'interests': 9,
        'media': 10,
      },
    }
  },
  computed: {
    dataEnvironments() {
      return this.$store.state.audience.dataEnvironments || []
    },
    sortedAttributes() {
      // Convert to array and sort by priority only
      return Object.entries(this.filteredSingleAttributes || this.singleAttributes)
        .map(([name, values]) => ({ name, values }))
        .filter(attr => attr.name !== '-') // Exclude the total count marker
        .sort((a, b) => {
          const priorityA = this.attributePriority[a.name] || 99
          const priorityB = this.attributePriority[b.name] || 99
          return priorityA - priorityB
        })
    },
    displayCount() {
      // Show filtered count when criteria are applied, otherwise show total
      return this.criteriaItems.length > 0 ? this.filteredCount : this.totalCount
    },
  },
  methods: {
    ...mapActions({
      fetchDataEnvironments: 'audience/fetchDataEnvironments',
      getDatasets: 'audience/getDatasets',
      runHistogramQuery: 'audience/runHistogramQuery',
      showModal: 'showModal',
      addToast: 'addToast',
    }),
    async loadDataEnvironments() {
      this.loadingEnvironments = true
      try {
        await this.fetchDataEnvironments()
      } catch (error) {
        this.error = 'Failed to load data environments'
      } finally {
        this.loadingEnvironments = false
      }
    },
    onDataEnvironmentChange() {
      this.selectedDataset = ''
      this.datasets = []
      this.resetResults()

      if (this.selectedDataEnvironment) {
        const env = this.dataEnvironments.find(e => e.name === this.selectedDataEnvironment)
        if (env && env.datasets) {
          this.datasets = env.datasets
        }
      }
    },
    onDatasetChange() {
      this.resetResults()
    },
    resetResults() {
      this.totalCount = 0
      this.singleAttributes = {}
      this.combinationAttributes = {}
      this.hasResults = false
      this.initialLoadComplete = false // Reset to allow animations on next load
      this.queryStatistics = null
    },
    async runFullHistogram() {
      if (!this.selectedDataEnvironment || !this.selectedDataset) return

      this.loadingHistogram = true
      this.error = null
      this.resetResults()

      try {
        // Run histogram with empty body to get all columns
        const queryBody = {
          dataset: this.selectedDataset,
        }

        const result = await this.runHistogramQuery({
          dataEnvironmentId: this.selectedDataEnvironment,
          queryBody,
          queryEngine: this.queryEngine,
        })

        // Capture query statistics
        if (result?.statistics) {
          this.queryStatistics = result.statistics
        }

        // Parse results
        this.parseHistogramResults(result)
        this.hasResults = this.totalCount > 0 || Object.keys(this.singleAttributes).length > 0

        // Allow animations for initial load, then disable for subsequent updates
        this.$nextTick(() => {
          setTimeout(() => {
            this.initialLoadComplete = true
          }, 1000) // Wait for initial animations to complete
        })
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Failed to run histogram analysis'
      } finally {
        this.loadingHistogram = false
      }
    },
    parseHistogramResults(result, filteredCriterionResult = false) {
      if (!result?.data?.body || !Array.isArray(result.data.body)) return

      const body = result.data.body
      const header = result.data.header || []
      const nameIndex = header.indexOf('attribute_name')
      const valueIndex = header.indexOf('attribute_value')
      const countIndex = header.indexOf('count')

      // First row is always total count
      if (!filteredCriterionResult && body.length > 0) {
        const firstRow = body[0]
        if (firstRow[nameIndex !== -1 ? nameIndex : 0] === '-') {
          this.totalCount = parseInt(firstRow[countIndex !== -1 ? countIndex : 2], 10) || 0
        }
      }

      // Group by attribute name
      const singleAttrs = {}
      const comboAttrs = {}

      body.forEach((row, index) => {
        const attrName = row[nameIndex !== -1 ? nameIndex : 0]
        const attrValue = row[valueIndex !== -1 ? valueIndex : 1]
        const count = parseInt(row[countIndex !== -1 ? countIndex : 2], 10) || 0

        // Skip the total count row
        if (attrName === '-' && attrValue === '-') return

        // Check if it's a combination attribute
        if (attrName.includes('{)(}')) {
          if (!comboAttrs[attrName]) comboAttrs[attrName] = []
          comboAttrs[attrName].push({ value: attrValue, count })
        } else {
          if (!singleAttrs[attrName]) singleAttrs[attrName] = []
          singleAttrs[attrName].push({ value: attrValue, count })
        }
      })

      // Sort values: ordinal attributes by leading number, others by count descending
      const ordinalAttributes = ['age_range', 'income_range', 'children', 'education']
      const extractLeadingNumber = (str) => {
        const match = String(str).match(/[\d.]+/)
        return match ? parseFloat(match[0]) : Infinity
      }
      Object.keys(singleAttrs).forEach(key => {
        if (ordinalAttributes.includes(key)) {
          singleAttrs[key].sort((a, b) => extractLeadingNumber(a.value) - extractLeadingNumber(b.value))
        } else {
          singleAttrs[key].sort((a, b) => b.count - a.count)
        }
      })
      Object.keys(comboAttrs).forEach(key => {
        comboAttrs[key].sort((a, b) => b.count - a.count)
      })

      if (filteredCriterionResult) {
        this.filteredSingleAttributes = singleAttrs
        this.filteredCombinationAttributes = comboAttrs
      } else {
        this.singleAttributes = singleAttrs
        this.combinationAttributes = comboAttrs
      }
    },
    formatAttributeName(name) {
      // Convert snake_case to Title Case
      return name
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    formatNumber(num) {
      if (num >= 1000000000) return (num / 1000000000).toFixed(1) + 'B'
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
      return num.toLocaleString()
    },
    formatExecutionTime(millis) {
      if (!millis && millis !== 0) return '-'
      if (millis < 1000) return `${millis}ms`
      if (millis < 60000) return `${(millis / 1000).toFixed(1)}s`
      const minutes = Math.floor(millis / 60000)
      const seconds = ((millis % 60000) / 1000).toFixed(0)
      return `${minutes}m ${seconds}s`
    },
    formatBytes(bytes) {
      if (!bytes && bytes !== 0) return '-'
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
      if (bytes < 1024 * 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
      return `${(bytes / (1024 * 1024 * 1024 * 1024)).toFixed(2)} TB`
    },
    // Criteria panel methods
    onChartSelect({ attribute, value }) {
      this.addCriterion(attribute, value)
    },
    addCriterion(attribute, value) {
      // Check if this criterion already exists
      const exists = this.criteriaItems.some(
        item => item.attribute === attribute && item.value === value
      )
      if (exists) {
        this.addToast({ type: 'info', message: 'This criterion is already added' })
        return
      }

      this.criteriaItems.push({ attribute, value })
      this.showCriteriaPanel = true
    },
    removeCriterion(index) {
      this.criteriaItems.splice(index, 1)
      if (this.criteriaItems.length === 0) {
        this.filteredCount = this.totalCount
        this.filteredSingleAttributes = this.filteredCombinationAttributes = null
      }
    },
    clearAllCriteria() {
      this.criteriaItems = []
      this.filteredCount = this.totalCount
      this.filteredSingleAttributes = this.filteredCombinationAttributes = null
    },
    closeCriteriaPanel() {
      this.showCriteriaPanel = false
    },
    getFilteredPercentage() {
      if (this.totalCount === 0) return 0
      return ((this.filteredCount / this.totalCount) * 100).toFixed(1)
    },
    buildCriterionFromItems() {
      if (this.criteriaItems.length === 0) return null

      // Group criteria by attribute
      const grouped = {}
      this.criteriaItems.forEach(item => {
        if (!grouped[item.attribute]) {
          grouped[item.attribute] = []
        }
        grouped[item.attribute].push(item.value)
      })

      // Build operands - 'in' operator is used for all attributes
      const operands = Object.entries(grouped).map(([column_name, values]) => {
        return {
          column_name,
          operator: 'in',
          values,
        }
      })

      // If multiple attributes, AND them together
      if (operands.length === 1) {
        return operands[0]
      }
      return {
        operator: 'and',
        operands
      }
    },
    async runFilteredHistogram() {
      if (!this.selectedDataEnvironment || !this.selectedDataset) return
      if (this.criteriaItems.length === 0) {
        this.filteredCount = this.totalCount
        return
      }

      this.loadingFiltered = true
      this.error = null

      try {
        const criterion = this.buildCriterionFromItems()
        const queryBody = {
          dataset: this.selectedDataset,
          criterion,
        }

        const result = await this.runHistogramQuery({
          dataEnvironmentId: this.selectedDataEnvironment,
          queryBody,
          queryEngine: this.queryEngine,
        })

        // Capture query statistics
        if (result?.statistics) {
          this.queryStatistics = result.statistics
        }

        // Extract the filtered count from results
        if (result?.data?.body && Array.isArray(result.data.body)) {
          const header = result.data.header || []
          const nameIndex = header.indexOf('attribute_name')
          const countIndex = header.indexOf('count')
          const firstRow = result.data.body[0]
          if (firstRow && firstRow[nameIndex !== -1 ? nameIndex : 0] === '-') {
            this.filteredCount = parseInt(firstRow[countIndex !== -1 ? countIndex : 2], 10) || 0
          }
        }

        // display filtered criterion results(charts only)
        this.parseHistogramResults(result, true)
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Failed to run histogram analysis'
        this.addToast({
          type: 'error',
          message: 'Failed to calculate filtered count'
        })
      } finally {
        this.loadingFiltered = false
      }
    },
    saveAsAudience() {
      if (this.criteriaItems.length === 0) return

      const criterion = this.buildCriterionFromItems()
      this.showModal({
        component: 'AddAudience',
        data: {
          prefillData: {
            audsum_data_environment: this.selectedDataEnvironment,
            audsum_dataset: this.selectedDataset,
            criterion: { criterion },
          }
        }
      })
    },
  },
  mounted() {
    this.loadDataEnvironments()
  },
  watch: {
    totalCount(newVal) {
      // Initialize filtered count when total count changes
      if (this.criteriaItems.length === 0) {
        this.filteredCount = newVal
      }
    }
  },
}
</script>

<style scoped>
.audience-dashboard {
  min-height: 400px;
  position: relative;
}

.audience-dashboard.panel-open .dashboard-content {
  margin-right: 310px;
  transition: margin-right 0.3s ease;
}

.audience-dashboard.panel-open .charts-section {
  --chart-columns: 3;
}

/* Criteria Panel Styles */
.criteria-panel {
  position: fixed;
  right: 0;
  top: 0;
  width: 300px;
  height: 100vh;
  background: #fff;
  box-shadow: -2px 0 20px rgba(0, 0, 0, 0.1);
  z-index: 1050;
  display: flex;
  flex-direction: column;
}

.criteria-panel-header {
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
}

.criteria-panel-body {
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
}

.filtered-count-card {
  background: linear-gradient(135deg, #f8f9fa, #e3e8ef);
  border-radius: 8px;
  padding: 1rem;
}

.filtered-count-card h3 {
  color: #2C7BE5;
  font-weight: 700;
}

.criteria-list {
  margin-top: 1rem;
}

.criteria-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.5rem;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #2C7BE5;
}

.criteria-item-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}

.criteria-attribute {
  font-weight: 600;
  color: #495057;
  font-size: 0.85rem;
}

.criteria-operator {
  color: #6c757d;
  font-size: 0.8rem;
}

.criteria-value {
  color: #2C7BE5;
  font-weight: 500;
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.criteria-actions {
  border-top: 1px solid #e9ecef;
  padding-top: 1rem;
}

/* Slide transition from right */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

/* Dashboard content */
.dashboard-content {
  min-width: 0;
  transition: margin-right 0.3s ease;
  overflow-x: hidden;
  max-width: 100%;
  box-sizing: border-box;
}

.total-count-card {
  max-width: 100%;
  box-sizing: border-box;
}

.total-count-card .card {
  background: linear-gradient(135deg, #2C7BE5 0%, #1a5cbd 100%);
  box-shadow: 0 4px 20px rgba(44, 123, 229, 0.3);
  max-width: 100%;
  box-sizing: border-box;
}

/* Query Statistics */
.query-stats {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

.stat-badge {
  background: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.9);
  padding: 0.25rem 0.6rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
}

/* Chart click hint */
.click-hint {
  opacity: 0.7;
  transition: opacity 0.3s;
}

.click-hint:hover {
  opacity: 1;
}

/* Charts loading overlay */
.charts-section {
  /* The tiles themselves live in AttributeCharts; a full-width page fits four
     across, three once the segment builder panel takes its 300px */
  --chart-columns: 4;
  min-height: 200px;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}

.charts-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(2px);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

.loading-content {
  text-align: center;
  padding: 2rem;
}

/* Fade transition for overlay */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
